import { useMemo, useState } from 'react';
import {
  Combobox,
  ScrollArea,
  UnstyledButton,
  useCombobox,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { Group } from '../../atoms/Group';
import { TextInput } from '../../atoms/TextInput';
import { Text } from '../../atoms/Text';
import {
  DEFAULT_PHONE_COUNTRY,
  dialCodeForCountry,
  listPhoneCountryOptions,
  normalizePhoneCountry,
  toNationalDigits,
  type CountryCode,
} from '../../utils/phone';

export type PhoneInputValue = {
  phone: string;
  country: CountryCode;
};

export interface PhoneInputProps {
  value?: string;
  country?: string | null;
  onChange: (next: PhoneInputValue) => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  /** Accessible label / search placeholder for the country picker */
  countrySearchPlaceholder?: string;
  testId?: string;
}

/** Above Mantine Modal/Drawer (~200–400) so the country list works in add modals. */
const COMBOBOX_Z_INDEX = 500;

export function PhoneInput({
  value = '',
  country,
  onChange,
  disabled,
  error,
  placeholder,
  countrySearchPlaceholder = 'Search country',
  testId,
}: PhoneInputProps) {
  const resolvedCountry = normalizePhoneCountry(country ?? DEFAULT_PHONE_COUNTRY);
  const dialCode = dialCodeForCountry(resolvedCountry);
  const options = useMemo(() => listPhoneCountryOptions(), []);
  const [search, setSearch] = useState('');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return options;
    return options.filter(
      (o) =>
        o.value.toLowerCase().includes(q) ||
        o.dialCode.includes(q) ||
        o.label.toLowerCase().includes(q),
    );
  }, [options, search]);

  const selected = options.find((o) => o.value === resolvedCountry);

  return (
    <div data-testid={testId}>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <Combobox
          store={combobox}
          withinPortal
          zIndex={COMBOBOX_Z_INDEX}
          onOptionSubmit={(v) => {
            onChange({
              phone: toNationalDigits(value),
              country: normalizePhoneCountry(v),
            });
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <UnstyledButton
              type="button"
              disabled={disabled}
              onClick={() => combobox.toggleDropdown()}
              aria-label={countrySearchPlaceholder}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                // Match Mantine Input `md` height so the country control aligns
                // with the phone field (and stays ≥16px text on iOS).
                height: 42,
                minWidth: 96,
                padding: '0 8px 0 10px',
                borderRadius: 'var(--mantine-radius-default)',
                border: '1px solid var(--mantine-color-gray-4)',
                background: disabled
                  ? 'var(--mantine-color-gray-1)'
                  : 'var(--mantine-color-body)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                flexShrink: 0,
              }}
            >
              <Text size="md" style={{ lineHeight: 1, whiteSpace: 'nowrap', fontSize: 16 }}>
                {selected?.flag ?? '🏳️'} +{dialCode}
              </Text>
              <IconChevronDown size={16} stroke={1.5} />
            </UnstyledButton>
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Search
              size="md"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={countrySearchPlaceholder}
            />
            <Combobox.Options>
              <ScrollArea.Autosize mah={240} type="scroll">
                {filtered.length === 0 ? (
                  <Combobox.Empty>No country found</Combobox.Empty>
                ) : (
                  filtered.map((o) => (
                    <Combobox.Option value={o.value} key={o.value} active={o.value === resolvedCountry}>
                      {o.flag} +{o.dialCode} · {o.value}
                    </Combobox.Option>
                  ))
                )}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        <TextInput
          style={{ flex: 1 }}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          inputMode="numeric"
          type="tel"
          autoComplete="tel-national"
          error={Boolean(error)}
          onChange={(e) =>
            onChange({
              phone: toNationalDigits(e.currentTarget.value),
              country: resolvedCountry,
            })
          }
        />
      </Group>
      {error ? (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      ) : null}
    </div>
  );
}
